import axios from 'axios';
import * as Sentry from '@sentry/react';

const addImageErrorListener = (container) => {
  if (!container) return () => {}; // Return no-op cleanup function

  // Function to attach error listener to an image
  const attachErrorListener = (image) => {
    // Skip if already has error listener
    if (image.dataset.errorListenerAttached) return;
    image.dataset.errorListenerAttached = 'true';

    // Attach error listener
    image.addEventListener('error', (e) => {
      if (e.target.dataset.errorHandled) return; // Prevent duplicate handling
      e.target.dataset.errorHandled = 'true';
      e.target.alt = 'Image failed to load. Our team has been notified.';
      e.target.style = 'background-color:transparent; color: #000;';
      e.target.className = '';
      handleImageLoadingError(e);
    });

    // Also listen for load events to mark as successful
    image.addEventListener('load', () => {
      image.dataset.errorHandled = 'false'; // Reset if it loads successfully
    });

    // Check if image is already broken (might have failed before listener attached)
    // Only check if image has a src and is not complete
    if (image.src && !image.src.startsWith('data:') && !image.complete) {
      // Set a timeout to check if image loaded successfully
      // If it's still not complete after a short delay, it likely failed
      setTimeout(() => {
        if (!image.complete || image.naturalHeight === 0) {
          // Image likely failed to load, create a synthetic error event
          const errorEvent = { 
            target: image, 
            currentSrc: image.src || image.currentSrc 
          };
          // Only trigger if we haven't already handled this error
          if (!image.dataset.errorHandled) {
            image.dataset.errorHandled = 'true';
            image.alt = 'Image failed to load. Our team has been notified.';
            image.style = 'background-color:#fff; color: #000;';
            image.className = '';
            handleImageLoadingError(errorEvent);
          }
        }
      }, 1000); // Check after 1 second
    }
  };

  // Attach listeners to existing images
  const images = container.querySelectorAll('img');
  images.forEach(attachErrorListener);

  // Use MutationObserver to catch dynamically added images
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) { // Element node
          if (node.tagName === 'IMG') {
            attachErrorListener(node);
          }
          // Also check for images within added nodes
          const newImages = node.querySelectorAll?.('img');
          if (newImages) {
            newImages.forEach(attachErrorListener);
          }
        }
      });
    });
  });

  observer.observe(container, {
    childList: true,
    subtree: true,
  });

  // Return cleanup function
  return () => observer.disconnect();
};

const handleImageLoadingError = (errorEvent) => {
  const imageUrl = errorEvent.target.currentSrc || errorEvent.target.src;
  
  // Log to console for debugging
  console.error('Image failed to load:', imageUrl, {
    complete: errorEvent.target.complete,
    naturalWidth: errorEvent.target.naturalWidth,
    naturalHeight: errorEvent.target.naturalHeight,
  });

  // Try to verify the URL is accessible (may fail due to CORS)
  axios
    .create()
    .get(imageUrl, { timeout: 5000 })
    .catch((error) => {
      Sentry.withScope((scope) => {
        scope.setContext('Error Information', {
          headers: error.config?.headers,
          message: error.message,
          response: error.request?.responseText,
          stack: error.stack,
          status: error.request?.status,
          url: error.config?.url || imageUrl,
          imageUrl,
          imageComplete: errorEvent.target.complete,
          imageNaturalWidth: errorEvent.target.naturalWidth,
          imageNaturalHeight: errorEvent.target.naturalHeight,
        });
        scope.setTag('exception', 'Image failed to load.');
        scope.setTag('failedImageURL', imageUrl);
        scope.setTag('errorType', error.code || 'unknown');
        
        // Capture the error
        Sentry.captureException(error);
      });
    })
    .catch(() => {
      // If axios itself fails (e.g., CORS), still log to Sentry
      Sentry.withScope((scope) => {
        scope.setContext('Error Information', {
          imageUrl,
          message: 'Image failed to load - possibly CORS or network issue',
          imageComplete: errorEvent.target.complete,
          imageNaturalWidth: errorEvent.target.naturalWidth,
          imageNaturalHeight: errorEvent.target.naturalHeight,
        });
        scope.setTag('exception', 'Image failed to load.');
        scope.setTag('failedImageURL', imageUrl);
        scope.setTag('errorType', 'cors_or_network');
        
        Sentry.captureMessage('Image loading failure - CORS or network issue', 'warning');
      });
    });
};

export default addImageErrorListener;
